import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Users, 
  Settings, 
  UserPlus, 
  UserMinus, 
  Edit3, 
  Save, 
  X, 
  ArrowLeft,
  Mail,
  Crown,
  Shield,
  User,
  Trash2,
  Loader,
  AlertCircle
} from 'lucide-react'
import { apiClient, handleApiError } from '@/lib/api'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'

interface TeamMember {
  id: any
  name: string
  email: string
  role: string
  joinedAt: string
  status: string
  user_id?: number
  uuid?: string
}

interface PendingInvitation {
  id: number
  uuid: string
  invited_email: string
  invitation_message: string
  status: string
  created_at: string
  expires_at: string
  invited_by_name: string
}

interface RecentJoin {
  id: number
  uuid: string
  invited_email: string
  status: string
  responded_at: string
  response_message: string
  joined_user_name: string
}

interface TeamDetails {
  id: number
  name: string
  description: string
  team_type: string
  max_members: number
  member_count: number
  is_active: boolean
  team_lead: {
    id: number
    name: string
    email: string
  } | null
  members: TeamMember[]
  // User permissions
  user_role: string
  can_delete_team: boolean
  can_add_members: boolean
  can_add_admins: boolean
  can_remove_members: boolean
  can_change_roles: boolean
}

export default function TeamManagePage() {
  const navigate = useNavigate()
  const params = useParams()
  const teamId = params.id as string
  const teamsEnabled = useFeatureFlag('recruiterTeams')

  const [team, setTeam] = useState<TeamDetails | null>(null)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([])
  const [recentJoins, setRecentJoins] = useState<RecentJoin[]>([])
  const [loading, setLoading] = useState(true)
  const [editingTeam, setEditingTeam] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [editedDescription, setEditedDescription] = useState('')
  const [showAddMember, setShowAddMember] = useState(false)
  const [newMemberEmail, setNewMemberEmail] = useState('')
  const [newMemberRole, setNewMemberRole] = useState('member')
  const [availableUsers, setAvailableUsers] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState<'members' | 'pending' | 'recent'>('members')
  const [currentUser, setCurrentUser] = useState<any>(null)

  const memberMatchesCurrentUser = (member: TeamMember) => {
    if (!currentUser) return false

    const memberIds = [member.id, member.user_id, member.uuid]
    const userIds = [currentUser.id, currentUser.uuid, currentUser.user_id, currentUser.userId]

    const idMatch = memberIds.some(mId =>
      mId !== undefined &&
      userIds.some(uId => uId !== undefined && String(mId).toLowerCase() === String(uId).toLowerCase())
    )

    if (idMatch) return true

    if (member.email && currentUser.email) {
      return member.email.toLowerCase() === String(currentUser.email).toLowerCase()
    }

    return false
  }

  useEffect(() => {
    const storedUser = localStorage.getItem('currentUser')
    if (storedUser) {
      try {
        setCurrentUser(JSON.parse(storedUser))
      } catch (err) {
        console.warn('Failed to parse currentUser from storage', err)
      }
    }

    if (!teamsEnabled) {
      return
    }
    if (teamId) {
      loadTeamData()
      loadAvailableUsers()
    }
  }, [teamId, teamsEnabled])

  const loadTeamData = async () => {
    if (!teamsEnabled) return
    try {
      setLoading(true)
      
      // Use the new comprehensive team management API
      const response = await apiClient.getTeamManagementData(teamId)
      if (response.success && response.data) {
        const { team: teamData, members, pending_invitations, recent_joins } = response.data
        
        setTeam({
          id: teamData.id || 0,
          name: teamData.name,
          description: teamData.description,
          team_type: 'standard',
          max_members: teamData.max_members || 10,
          member_count: members?.length || 0,
          is_active: true,
          team_lead: teamData.team_lead,
          members: [],
          // User permissions from API
          user_role: teamData.user_role || 'member',
          can_delete_team: teamData.can_delete_team || false,
          can_add_members: teamData.can_add_members || false,
          can_add_admins: teamData.can_add_admins || false,
          can_remove_members: teamData.can_remove_members || false,
          can_change_roles: teamData.can_change_roles || false
        })
        setEditedName(teamData.name)
        setEditedDescription(teamData.description || '')
        
        // Transform and set members data
        if (members) {
          const transformedMembers = members.map((member: any) => ({
            id: member.user_uuid || member.id,
            name: member.full_name || `${member.first_name} ${member.last_name}`.trim(),
            email: member.email,
            role: member.role || 'member',
            joinedAt: member.joined_at,
            status: member.status || 'active'
          }))
          setMembers(transformedMembers)
        }
        
        // Set pending invitations
        if (pending_invitations) {
          setPendingInvitations(pending_invitations)
        }
        
        // Set recent joins
        if (recent_joins) {
          setRecentJoins(recent_joins)
        }
      }
    } catch (error) {
      console.error('Failed to load team data:', error)
      // Fallback to original API calls if the new endpoint fails
      try {
        const teamResponse = await apiClient.getTeamDetails(teamId)
        if (teamResponse.success && teamResponse.data?.team) {
          const teamData = teamResponse.data.team
          setTeam({
            ...teamData,
            // Default permissions for fallback
            user_role: 'member',
            can_delete_team: false,
            can_add_members: false,
            can_add_admins: false,
            can_remove_members: false,
            can_change_roles: false
          })
          setEditedName(teamData.name)
          setEditedDescription(teamData.description || '')
        }
        
        const membersResponse = await apiClient.getTeamMembers(teamId)
        if (membersResponse.success && membersResponse.data?.members) {
          setMembers(membersResponse.data.members)
        }
      } catch (fallbackError) {
        console.error('Fallback API calls also failed:', fallbackError)
      }
    } finally {
      setLoading(false)
    }
  }

  const loadAvailableUsers = async () => {
    if (!teamsEnabled) return
    try {
      const response = await apiClient.getUsers({ sameTeam: false })
      if (response.success && response.data) {
        setAvailableUsers(Array.isArray(response.data) ? response.data : (response.data as any).users || [])
      }
    } catch (error) {
      console.error('Failed to load available users:', error)
    }
  }

  const handleUpdateTeam = async () => {
    try {
      const response = await apiClient.updateTeam(teamId, {
        name: editedName,
        description: editedDescription
      })
      
      if (response.success) {
        setTeam(prev => prev ? { ...prev, name: editedName, description: editedDescription } : null)
        setEditingTeam(false)
      } else {
        alert('Failed to update team: ' + handleApiError(response))
      }
    } catch (error) {
      alert('Failed to update team: ' + handleApiError(error))
    }
  }

  const handleAddMember = async () => {
    try {
      if (!newMemberEmail || !newMemberEmail.includes('@')) {
        alert('Please enter a valid email address')
        return
      }

      // Send team invitation via email
      const response = await apiClient.sendTeamInvitation({
        invited_email: newMemberEmail,
        team_id: teamId,  // Use UUID string directly instead of parseInt
        invitation_message: `You've been invited to join the ${team?.name} team`
      })
      
      if (response.success) {
        alert(`Invitation sent to ${newMemberEmail}`)
        setShowAddMember(false)
        setNewMemberEmail('')
        setNewMemberRole('member')
        // Refresh team data to show the new invitation in pending list
        loadTeamData()
      } else {
        alert('Failed to send invitation: ' + handleApiError(response))
      }
    } catch (error) {
      alert('Failed to send invitation: ' + handleApiError(error))
    }
  }

  const handleRemoveMember = async (userId: any, userName: string) => {
    if (memberMatchesCurrentUser({ id: userId, name: userName, email: '', role: '', joinedAt: '', status: '' })) {
      alert('You cannot remove yourself from the team. At least one admin must remain.')
      return
    }

    if (!window.confirm(`Remove ${userName} from the team?`)) return

    try {
      const response = await apiClient.removeTeamMember(teamId, userId)
      
      if (response.success) {
        setMembers(prev => prev.filter(member => member.id !== userId))
      } else {
        alert('Failed to remove team member: ' + handleApiError(response))
      }
    } catch (error) {
      alert('Failed to remove team member: ' + handleApiError(error))
    }
  }

  const handleRoleChange = async (memberId: number, newRole: string) => {
    const member = members.find(m => m.id === memberId)
    if (!member || member.role === newRole) return

    if (!window.confirm(`Change ${member.name}'s role from ${member.role} to ${newRole}?`)) return

    try {
      const response = await apiClient.changeTeamMemberRole(teamId, memberId, newRole)
      
      if (response.success) {
        // Update the member's role in the local state
        setMembers(prev => prev.map(m => 
          m.id === memberId ? { ...m, role: newRole } : m
        ))
        alert(`Successfully changed ${member.name}'s role to ${newRole}`)
      } else {
        alert('Failed to change member role: ' + handleApiError(response))
      }
    } catch (error) {
      alert('Failed to change member role: ' + handleApiError(error))
    }
  }

  const handleCancelInvitation = async (invitationId: number, email: string) => {
    if (!window.confirm(`Cancel invitation for ${email}?`)) return

    try {
      const response = await apiClient.request(`/team-invitations/${invitationId}`, {
        method: 'DELETE'
      })
      
      if (response.success) {
        // Refresh team data to show the updated pending invitations list
        loadTeamData()
        alert(`Invitation for ${email} has been cancelled`)
      } else {
        alert('Failed to cancel invitation')
      }
    } catch (error) {
      alert('Failed to cancel invitation')
    }
  }

  const handleDeleteTeam = async () => {
    if (!team) return

    try {
      const response = await apiClient.deleteTeam(teamId)
      
      if (response.success) {
        alert(`Team "${team.name}" has been successfully deleted.`)
        navigate('/recruiter/teams')
      } else {
        alert('Failed to delete team: ' + handleApiError(response))
      }
    } catch (error) {
      alert('Failed to delete team: ' + handleApiError(error))
    }
  }

  if (!teamsEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] text-center space-y-4">
        <AlertCircle className="w-10 h-10 text-gray-400" />
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Teams coming soon</h1>
          <p className="text-gray-600 mt-2 max-w-lg">
            Team management is disabled in this environment while we finalize the experience.
          </p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader className="h-6 w-6 animate-spin text-blue-600" />
          <span>Loading team details...</span>
        </div>
      </div>
    )
  }

  if (!team) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Team Not Found</h2>
          <p className="text-gray-600 mb-4">The team you're looking for doesn't exist or you don't have access to it.</p>
          <button 
            onClick={() => navigate('/recruiter/teams')}
            className="text-blue-600 hover:text-blue-800"
          >
            ← Back to Teams
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/recruiter/teams')}
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Team Management</h1>
                <p className="text-sm text-gray-500">Manage {team.name} team details and members</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                team.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {team.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Team Details Section */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Settings className="h-5 w-5 mr-2" />
                  Team Details
                </h2>
                <button
                  onClick={() => setEditingTeam(!editingTeam)}
                  className="text-blue-600 hover:text-blue-800 p-1"
                >
                  {editingTeam ? <X className="h-4 w-4" /> : <Edit3 className="h-4 w-4" />}
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
                  {editingTeam ? (
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900">{team.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  {editingTeam ? (
                    <textarea
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="text-gray-600">{team.description || 'No description'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Team Type</label>
                  <p className="text-gray-900 capitalize">{team.team_type}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Members</label>
                  <p className="text-gray-900">{team.max_members}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Members</label>
                  <p className="text-gray-900">{team.member_count} / {team.max_members}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Role</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${
                    team.user_role === 'admin' ? 'bg-purple-100 text-purple-800' :
                    team.user_role === 'lead' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {team.user_role}
                  </span>
                </div>

                {editingTeam && (
                  <div className="pt-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={handleUpdateTeam}
                        className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingTeam(false)
                          setEditedName(team.name)
                          setEditedDescription(team.description || '')
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                    
                    {/* Delete Team Section - Only shown when editing */}
                    {team?.can_delete_team && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3 flex-1">
                              <h3 className="text-sm font-medium text-red-800">
                                Delete Team
                              </h3>
                              <div className="mt-2 text-sm text-red-700">
                                <p>
                                  This action <strong>cannot be undone</strong>. This will permanently delete the team "{team.name}" and remove all team members and associated data.
                                </p>
                              </div>
                              <div className="mt-4">
                                <button
                                  onClick={() => {
                                    const confirmMessage = `Are you sure you want to permanently delete the team "${team.name}"?\n\nThis action CANNOT be undone and will:\n• Remove all team members\n• Delete all team data\n• Cannot be restored\n\nType "DELETE" in the next prompt to confirm.`
                                    if (window.confirm(confirmMessage)) {
                                      const confirmation = prompt('Type "DELETE" to confirm team deletion:')
                                      if (confirmation === 'DELETE') {
                                        handleDeleteTeam()
                                      } else {
                                        alert('Team deletion cancelled. You must type "DELETE" exactly to confirm.')
                                      }
                                    }
                                  }}
                                  className="flex items-center px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Team Permanently
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Team Members Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg shadow-sm"
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Users className="h-5 w-5 mr-2" />
                    Team Management
                  </h2>
                  {team?.can_add_members && (
                    <button
                      onClick={() => setShowAddMember(true)}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Member
                    </button>
                  )}
                </div>
                
                {/* Tab Navigation */}
                <div className="flex space-x-4">
                  <button
                    onClick={() => setActiveTab('members')}
                    className={`px-4 py-2 text-sm font-medium rounded-md ${
                      activeTab === 'members'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Active Members ({members.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('pending')}
                    className={`px-4 py-2 text-sm font-medium rounded-md ${
                      activeTab === 'pending'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Pending Invitations ({pendingInvitations.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('recent')}
                    className={`px-4 py-2 text-sm font-medium rounded-md ${
                      activeTab === 'recent'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Recent Joins ({recentJoins.length})
                  </button>
                </div>
              </div>

              {/* Add Member Form */}
              {showAddMember && (
                <div className="p-6 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Member</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={newMemberEmail}
                        onChange={(e) => setNewMemberEmail(e.target.value)}
                        placeholder="Enter email address to invite..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                      <select
                        value={newMemberRole}
                        onChange={(e) => setNewMemberRole(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="member">Member</option>
                        {team?.can_add_admins && <option value="lead">Team Lead</option>}
                        {team?.can_add_admins && <option value="admin">Admin</option>}
                      </select>
                    </div>
                  </div>
                  <div className="flex space-x-2 mt-4">
                    <button
                      onClick={handleAddMember}
                      disabled={!newMemberEmail}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Member
                    </button>
                    <button
                      onClick={() => {
                        setShowAddMember(false)
                        setNewMemberEmail('')
                        setNewMemberRole('member')
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Content Area */}
              <div className="divide-y divide-gray-200">
                {/* Active Members Tab */}
                {activeTab === 'members' && (
                  <>
                    {members.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No team members found</p>
                      </div>
                    ) : (
                      members.map((member, index) => (
                        <motion.div
                          key={member.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="p-6 hover:bg-gray-50"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className={`p-2 rounded-full ${
                                member.role === 'lead' ? 'bg-yellow-100' :
                                member.role === 'admin' ? 'bg-purple-100' : 'bg-blue-100'
                              }`}>
                                {member.role === 'lead' ? <Crown className="h-4 w-4 text-yellow-600" /> :
                                 member.role === 'admin' ? <Shield className="h-4 w-4 text-purple-600" /> :
                                 <User className="h-4 w-4 text-blue-600" />}
                              </div>
                              <div>
                                <h3 className="text-sm font-medium text-gray-900">{member.name}</h3>
                                <p className="text-sm text-gray-500">{member.email}</p>
                              </div>
                              <div className="ml-4">
                                {team?.can_change_roles ? (
                                  <select
                                    value={member.role}
                                    onChange={(e) => handleRoleChange(member.id, e.target.value)}
                                    className="px-2 py-1 text-xs font-medium rounded border-0 bg-transparent capitalize cursor-pointer"
                                    style={{
                                      backgroundColor: member.role === 'admin' ? '#f3e8ff' : 
                                                     member.role === 'lead' ? '#fef3c7' : '#dbeafe',
                                      color: member.role === 'admin' ? '#7c3aed' : 
                                            member.role === 'lead' ? '#d97706' : '#2563eb'
                                    }}
                                  >
                                    <option value="member">Member</option>
                                    <option value="lead">Team Lead</option>
                                    <option value="admin">Admin</option>
                                  </select>
                                ) : (
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
                                    member.role === 'lead' ? 'bg-yellow-100 text-yellow-800' :
                                    member.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                                    'bg-blue-100 text-blue-800'
                                  }`}>
                                    {member.role}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                member.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {member.status}
                              </span>
                              
                              {team?.can_remove_members && !memberMatchesCurrentUser(member) && (
                                // Admins can remove anyone, Team leads can remove members but not other admins
                                (team.user_role === 'admin' || (team.user_role === 'lead' && member.role !== 'admin')) && (
                                  <button
                                    onClick={() => handleRemoveMember(member.id, member.name)}
                                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                                    title="Remove member"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                          
                          {member.joinedAt && (
                            <div className="mt-2 text-xs text-gray-500">
                              Joined: {new Date(member.joinedAt).toLocaleDateString()}
                            </div>
                          )}
                        </motion.div>
                      ))
                    )}
                  </>
                )}

                {/* Pending Invitations Tab */}
                {activeTab === 'pending' && (
                  <>
                    {pendingInvitations.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <Mail className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No pending invitations</p>
                      </div>
                    ) : (
                      pendingInvitations.map((invitation, index) => (
                        <motion.div
                          key={invitation.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="p-6 hover:bg-gray-50"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="p-2 rounded-full bg-orange-100">
                                <Mail className="h-4 w-4 text-orange-600" />
                              </div>
                              <div>
                                <h3 className="text-sm font-medium text-gray-900">{invitation.invited_email}</h3>
                                <p className="text-sm text-gray-500">Invited by {invitation.invited_by_name}</p>
                                {invitation.invitation_message && (
                                  <p className="text-xs text-gray-400 mt-1">{invitation.invitation_message}</p>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 capitalize">
                                {invitation.status}
                              </span>
                              <button
                                onClick={() => handleCancelInvitation(invitation.id, invitation.invited_email)}
                                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                                title="Cancel invitation"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          
                          <div className="mt-2 text-xs text-gray-500">
                            <div>Sent: {new Date(invitation.created_at).toLocaleDateString()}</div>
                            <div>Expires: {new Date(invitation.expires_at).toLocaleDateString()}</div>
                          </div>
                        </motion.div>
                      ))
                    )}
                  </>
                )}

                {/* Recent Joins Tab */}
                {activeTab === 'recent' && (
                  <>
                    {recentJoins.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <UserPlus className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No recent joins via invitation</p>
                      </div>
                    ) : (
                      recentJoins.map((join, index) => (
                        <motion.div
                          key={join.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="p-6 hover:bg-gray-50"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="p-2 rounded-full bg-green-100">
                                <UserPlus className="h-4 w-4 text-green-600" />
                              </div>
                              <div>
                                <h3 className="text-sm font-medium text-gray-900">
                                  {join.joined_user_name || join.invited_email}
                                </h3>
                                <p className="text-sm text-gray-500">{join.invited_email}</p>
                                {join.response_message && (
                                  <p className="text-xs text-gray-400 mt-1">"{join.response_message}"</p>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 capitalize">
                                {join.status}
                              </span>
                            </div>
                          </div>
                          
                          <div className="mt-2 text-xs text-gray-500">
                            Joined: {new Date(join.responded_at).toLocaleDateString()}
                          </div>
                        </motion.div>
                      ))
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}
